/**
 * Algolia Frequently Bought Together Widget
 * Uses Algolia Recommend API to show products frequently purchased together
 * Falls back to search-based recommendations if Recommend is not available
 */

(function() {
  'use strict';

  // Bot detection: do not initialize on crawlers to avoid unnecessary Algolia operations
  const isBot = /bot|crawler|spider|googlebot|bingbot|slurp|duckduckbot|facebot|ia_archiver/i.test(navigator.userAgent);
  if (isBot) return;

  // Configuration
  const CONTAINER_SELECTOR = '#algolia-frequently-bought-together';
  const MAX_RECOMMENDATIONS = 20; // Maximum products to fetch
  const INITIAL_VISIBLE = 20; // Products visible initially
  const LOAD_MORE_INCREMENT = 4; // Products to show on "Load More" click
  const RETRY_DELAY = 200;
  const MAX_RETRIES = 15;

  // ===================================================================
  // ALGOLIA TRANSLATIONS DICTIONARY
  // ===================================================================
  const ALGOLIA_TRANSLATIONS = {
    get it() {
      return window.AlgoliaTranslations.CollectionIT;
    },
    get en() {
      return window.AlgoliaTranslations.CollectionEN;
    },
    get fr() {
      return window.AlgoliaTranslations.CollectionFR;
    },
    get de() {
      return window.AlgoliaTranslations.CollectionDE;
    },
    get es() {
      return window.AlgoliaTranslations.CollectionES;
    },
    get zh() {
      return window.AlgoliaTranslations.CollectionZH;
    },
  };

  /**
   * Get current product's objectID from page
   * For Algolia, objectID = variant ID (not product ID)
   */
  function getCurrentProductObjectID() {
    // First try: get variant ID from container data attribute
    const container = document.querySelector(CONTAINER_SELECTOR);
    if (container && container.dataset.variantId) {
      return container.dataset.variantId;
    }

    // Fallback 1: Try to get variant ID from form
    const variantIdInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (variantIdInput && variantIdInput.value) {
      return variantIdInput.value;
    }

    // Fallback 2: Try old data-product-id (might be variant actually)
    if (container && container.dataset.productId) {
      return container.dataset.productId;
    }

    console.error('❌ Could not find product objectID');
    return null;
  }

  /**
   * Get Shopify product ID (different from objectID/variant ID)
   */
  function getShopifyProductID() {
    const container = document.querySelector(CONTAINER_SELECTOR);
    return container?.dataset.productId || null;
  }

  /**
   * Get current language from page
   */
  function getCurrentLanguage() {
    const lang = document.documentElement.lang.split('-')[0] || 'it';
    return lang;
  }
  
  /**
   * Get translated text for a given key
   */
  function t(key, replacements = {}) {
    const translations =
      ALGOLIA_TRANSLATIONS[getCurrentLanguage()] || ALGOLIA_TRANSLATIONS["it"];
    // Supporto chiavi annidate tipo "filters.no_year"
    const keys = key.split(".");
    let text = translations;
    for (let k of keys) {
      if (text && typeof text === "object" && k in text) {
        text = text[k];
      } else {
        text = key;
        break;
      }
    }
    // Apply replacements for dynamic content
    if (typeof text === "string") {
      Object.keys(replacements).forEach((placeholder) => {
        text = text.replace(`{${placeholder}}`, replacements[placeholder]);
      });
    }
    return text;
  }

  /**
   * Get cookie value by name
   */
  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

    /**
   * Get market configuration by shipping destination country code
   */
    function getMarketByShippingDestination(countryCode) {
      const country = countryCode.toLowerCase();
  
      // Direct market mapping based on shipping destination
      const marketMappings = {
        // Major specific markets
        us: { suffix: "usa_en", field: "market_pricing_usd", key: "usa" },
        gb: {
          suffix: "united_kingdom_en",
          field: "market_pricing_gbp",
          key: "united_kingdom",
        },
        uk: {
          suffix: "united_kingdom_en",
          field: "market_pricing_gbp",
          key: "united_kingdom",
        },
        ch: {
          suffix: "switzerland_de",
          field: "market_pricing_chf",
          key: "switzerland",
        },
        hk: {
          suffix: "asia_hong_kong_en",
          field: "market_pricing_hkd",
          key: "hong_kong",
        },
        dk: { suffix: "denmark_en", field: "market_pricing_dkk", key: "denmark" },
        se: { suffix: "sweden_en", field: "market_pricing_sek", key: "sweden" },
        be: { suffix: "belgium_en", field: "market_pricing_eur", key: "belgium" },
        nl: {
          suffix: "netherlands_en",
          field: "market_pricing_eur",
          key: "netherlands",
        },
        de: { suffix: "germany_de", field: "market_pricing_eur", key: "germany" },
        fr: { suffix: "france_fr", field: "market_pricing_eur", key: "france" },
  
        // European countries with specific pricing
        at: { suffix: "europe_en", field: null, key: "austria" }, // Austria uses standard EUR pricing
        bg: { suffix: "europe_en", field: null, key: "bulgaria" }, // Bulgaria uses standard EUR pricing
        ad: { suffix: "europe_en", field: null, key: "andorra" }, // Andorra uses standard EUR pricing
  
        // All other EU countries - standard EUR pricing
        cy: { suffix: "europe_en", field: null, key: "cyprus" }, // Cyprus
        pl: { suffix: "europe_en", field: null, key: "poland" }, // Poland
        cz: { suffix: "europe_en", field: null, key: "czech_republic" }, // Czech Republic
        hu: { suffix: "europe_en", field: null, key: "hungary" }, // Hungary
        ro: { suffix: "europe_en", field: null, key: "romania" }, // Romania
        sk: { suffix: "europe_en", field: null, key: "slovakia" }, // Slovakia
        si: { suffix: "europe_en", field: null, key: "slovenia" }, // Slovenia
        hr: { suffix: "europe_en", field: null, key: "croatia" }, // Croatia
        lt: { suffix: "europe_en", field: null, key: "lithuania" }, // Lithuania
        lv: { suffix: "europe_en", field: null, key: "latvia" }, // Latvia
        ee: { suffix: "europe_en", field: null, key: "estonia" }, // Estonia
        mt: { suffix: "europe_en", field: null, key: "malta" }, // Malta
        lu: { suffix: "europe_en", field: null, key: "luxembourg" }, // Luxembourg
        ie: { suffix: "europe_en", field: null, key: "ireland" }, // Ireland
        pt: { suffix: "europe_en", field: null, key: "portugal" }, // Portugal
        gr: { suffix: "europe_en", field: null, key: "greece" }, // Greece
  
        // Extra-UE countries (map to extra_ue_en index)
        au: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        ca: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        nz: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        jp: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        kr: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        sg: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        mx: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        br: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        ar: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        cl: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        za: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        in: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        th: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        my: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        ph: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        vn: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        id: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        tw: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        ae: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        il: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        tr: {
          suffix: "extra_ue_en",
          field: "market_pricing_eur",
          key: "extra_ue",
        },
        fi: { 
          suffix: "finland_en",
          field: "market_pricing_eur", 
          key: "finland" 
        }
      };
  
      const mapping = marketMappings[country];
      if (mapping) {
        return {
          key: mapping.key,
          suffix: mapping.suffix,
          pricingField: mapping.field,
          matchedPattern: `localization=${countryCode.toUpperCase()}`,
          isLocalizationBased: true,
          isCrossBorder: true,
        };
      }
  
      return null;
    }

  /**
   * Detect current market from URL and cookies
   * Returns market suffix like "italy_it", "usa_en", "united_kingdom_en", etc.
   */
  function detectMarketSuffix() {
    const pathname = window.location.pathname.toLowerCase();
    const url = window.location.href.toLowerCase();
    
    // Check localization cookie first (shipping destination) – same logic as algolia-collection
    const localization = getCookie('localization');
    if (localization) {
      const shippingMarket = getMarketByShippingDestination(localization);
      if (shippingMarket && shippingMarket.suffix) {
        return shippingMarket.suffix;
      }
    }

    // Check URL patterns - same priority order as algolia-collection.js
    // Belgium
    if (pathname.includes('/fr-be/') || pathname.includes('/en-be/')) return 'belgium_en';
    
    // Switzerland (multi-language)
    if (pathname.includes('/fr-ch/')) return 'switzerland_fr';
    if (pathname.includes('/it-ch/')) return 'switzerland_it';
    if (pathname.includes('/en-ch/') || pathname.includes('/de-ch/')) return 'switzerland_de';
    
    // USA
    if (pathname.includes('/en-us/')) return 'usa_en';
    
    // UK
    if (pathname.includes('/en-gb/')) return 'united_kingdom_en';
    
    // Hong Kong
    if (pathname.includes('/en-hk/')) return 'asia_hong_kong_en';
    
    // Denmark
    if (pathname.includes('/da-dk/')) return 'denmark_en';
    
    // Sweden
    if (pathname.includes('/sv-se/')) return 'sweden_en';
    
    // Finland
    if (pathname.includes('/fi-fi/')) return 'finland_en';
    
    // Germany
    if (pathname.includes('/de-de/') || pathname.includes('/de/')) return 'germany_de';
    
    // France
    if (pathname.includes('/fr-fr/') || pathname.includes('/fr/')) return 'france_fr';
    
    // Netherlands
    if (pathname.includes('/en-nl/') || pathname.includes('/de-nl/') || pathname.includes('/nl/')) return 'netherlands_en';

    // Italy explicit
    if (pathname.includes('/it/')) return 'italy_it';
    
    // Default fallback: Italy
    return 'italy_it';
  }

  /**
   * Build market-specific index name
   */
  function getMarketSpecificIndex(baseIndexName) {
    if (!baseIndexName) return null;
    
    const marketSuffix = detectMarketSuffix();
    const indexName = `${baseIndexName}_${marketSuffix}`;
    
    return indexName;
  }

    /**
 * Get the two-letter market reference code (e.g., "BE", "DK") based on the current market.
 * Used primarily for constructing metafield keys (e.g., omnibus_price-EU).
 */
    function getMarketReference() {
      if (typeof window.shopifyCurrentMarket === "undefined") {
        // Fallback to URL path/locale for robust detection outside of specific contexts
        const lang = URLUtils.extractLanguageFromURL() || "it";
  
        // Map common EU/Non-EU markets based on simplified locale/URL logic
        switch (lang.toLowerCase()) {
          case "fr":
            return "FR";
          case "de":
            return "DE";
          case "es":
            return "ES";
          case "pt":
            return "PT";
          case "en":
            // For EN, check if it's a known non-EUR market (like HK, GB, US) via URL path/query
            const pathname = window.location.pathname.toLowerCase();
            if (pathname.includes("/en-hk/") || pathname.includes("/hk"))
              return "HK";
            if (pathname.includes("/en-gb/") || pathname.includes("/uk"))
              return "GB";
            if (pathname.includes("/en-us/") || pathname.includes("/us"))
              return "US";
            // Default EN market is usually Europe (EUR)
            return "EU";
          case "it":
          default:
            // Default or generic language often implies a known primary market or EU/Extra-UE logic
            return "IT";
        }
      }
  
      // Use the global Shopify market variable if available
      switch (window.shopifyCurrentMarket.toLowerCase()) {
        case "belgium":
          return "BE";
        case "denmark":
          return "DK";
        case "germany":
          return "DE";
        case "europe":
          return "EU";
        case "finland":
          return "FI";
        case "france":
          return "FR";
        case "italy":
        case "it":
          return "IT";
        case "netherlands":
          return "NL";
        case "sweden":
          return "SE";
        case "united_kingdom":
        case "uk":
          return "GB";
        case "usa":
        case "united_states":
          return "US";
        case "hong_kong":
          return "HK";
        case "switzerland":
          return "CH";
        default:
          // Fallback for non-specific markets, often default to Europe
          return "";
      }
    }

      /**
 * Normalizza la descrizione del formato da "0,75 l" a "75cl"
 */
  function normalizeFormatDescription(format) {
    if (!format) return "";

    // Converti "0,75 l" o "0.75 l" a "75cl"
    const literMatch = format.match(/(\d+[.,]\d+)\s*l/i);
    if (literMatch) {
      const liters = parseFloat(literMatch[1].replace(",", "."));
      const centiliters = Math.round(liters * 100);
      return `${centiliters}cl`;
    }

    // Converti "750 ml" a "75cl"
    const mlMatch = format.match(/(\d+)\s*ml/i);
    if (mlMatch) {
      const ml = parseInt(mlMatch[1]);
      const cl = Math.round(ml / 10);
      return `${cl}cl`;
    }

    // Se è già in cl, mantieni il formato
    if (format.match(/\d+\s*cl/i)) {
      return format.replace(/(\d+)\s*cl/i, "$1cl");
    }

    // Se non riconosce il formato, restituisci l'originale
    return format;
  }

  /**
   * Get market-specific URL prefix
   * Returns the URL prefix for the current market (e.g., '/en-us/', '/fr-ch/', '/')
   */
  function getMarketUrlPrefix() {
    const pathname = window.location.pathname.toLowerCase();
    
    // Check for specific market patterns in URL
    const marketPatterns = [
      '/en-us/', '/en-gb/', '/en-hk/',
      '/fr-ch/', '/de-ch/', '/it-ch/', '/en-ch/',
      '/fr-be/', '/en-be/',
      '/da-dk/', '/sv-se/', '/fi-fi/',
      '/de-de/', '/fr-fr/', '/en-nl/', '/de-nl/',
      '/fr/', '/de/', '/nl/', '/it/', '/es/'
    ];
    
    for (const pattern of marketPatterns) {
      if (pathname.includes(pattern)) {
        return pattern;
      }
    }
    
    // Default: no prefix (base market)
    return '/';
  }

  /**
   * Get market-specific pricing field
   * Returns the appropriate pricing field for current market (e.g., 'market_pricing_usd')
   */
  function getMarketPricingField() {
    const pathname = window.location.pathname.toLowerCase();
    const url = window.location.href.toLowerCase();
    
    // Switzerland - all language variants use CHF
    if (pathname.includes('/fr-ch/') || pathname.includes('/de-ch/') || 
        pathname.includes('/it-ch/') || pathname.includes('/en-ch/') ||
        url.includes('/fr-ch/') || url.includes('/de-ch/') || 
        url.includes('/it-ch/') || url.includes('/en-ch/')) {
      return 'market_pricing_chf';
    }
    
    // Check for USA market
    if (pathname.includes('/en-us/') || pathname.includes('/usa/') ||
        url.includes('/en-us/') || url.includes('/usa/')) {
      return 'market_pricing_usd';
    }
    
    // Check for UK market
    if (pathname.includes('/en-gb/') || pathname.includes('/uk/') ||
        url.includes('/en-gb/') || url.includes('/uk/')) {
      return 'market_pricing_gbp';
    }
    
    // Check for Hong Kong market
    if (pathname.includes('/en-hk/') || pathname.includes('/hk/') ||
        url.includes('/en-hk/') || url.includes('/hk/')) {
      return 'market_pricing_hkd';
    }
    
    // Check for Denmark market
    if (pathname.includes('/da-dk/') || pathname.includes('/en-dk/') || url.includes('/da-dk/') || url.includes('/en-dk/')) {
      return 'market_pricing_dkk';
    }
    
    // Check for Sweden market
    if (pathname.includes('/sv-se/') || url.includes('/sv-se/')) {
      return 'market_pricing_sek';
    }
    
    // Belgium → market_pricing_eur
    if (pathname.includes('/en-be/') || pathname.includes('/fr-be/') ||
        url.includes('/en-be/') || url.includes('/fr-be/')) {
      return 'market_pricing_eur';
    }
    
    // France → market_pricing_eur
    if (pathname.includes('/fr-fr/') || pathname.includes('/fr/') ||
        url.includes('/fr-fr/') || url.includes('/fr/')) {
      return 'market_pricing_eur';
    }
    
    // Germany → market_pricing_eur
    if (pathname.includes('/de-de/') || pathname.includes('/de/') ||
        url.includes('/de-de/') || url.includes('/de/')) {
      return 'market_pricing_eur';
    }
    
    // Netherlands → market_pricing_eur
    if (pathname.includes('/en-nl/') || pathname.includes('/de-nl/') || pathname.includes('/nl/') ||
        url.includes('/en-nl/') || url.includes('/de-nl/') || url.includes('/nl/')) {
      return 'market_pricing_eur';
    }
    
    // Finland → market_pricing_eur
    if (pathname.includes('/fi-fi/') || url.includes('/fi-fi/')) {
      return 'market_pricing_eur';
    }
    
    // NOTE: /it/ (Italy) and /es/ (Spain) use null (base hit.price)
    
    // Check localization cookie first (shipping destination) – same as algolia-collection
    const localization = getCookie('localization');
    if (localization) {
      const loc = localization.toLowerCase();
      if (loc === 'it') return 'market_pricing_eur';
      
      const shippingMarket = getMarketByShippingDestination(localization);
      if (shippingMarket) {
        // field can be null for EU markets that use base hit.price (e.g. Austria, Bulgaria)
        if (shippingMarket.pricingField) return shippingMarket.pricingField;
        // EU/extra-EU with explicit market_pricing_eur
        if (shippingMarket.suffix === 'europe_en' || shippingMarket.suffix === 'extra_ue_en') {
          return 'market_pricing_eur';
        }
        return null;
      }
    }
    
    // Fallback: Use Shopify.currency.active for non-EUR markets (localhost/preview)
    if (typeof window.Shopify !== 'undefined' && window.Shopify.currency) {
      const currency = (window.Shopify.currency.active || window.Shopify.currency).toUpperCase();
      const currencyToField = {
        'USD': 'market_pricing_usd',
        'GBP': 'market_pricing_gbp',
        'HKD': 'market_pricing_hkd',
        'CHF': 'market_pricing_chf',
        'DKK': 'market_pricing_dkk',
        'SEK': 'market_pricing_sek',
      };
      if (currencyToField[currency]) return currencyToField[currency];
    }
    
    // Default: null (Italy and unknown EUR markets use base hit.price)
    return null;
  }

  /**
   * Show error message in container
   */
  function showError(message) {
    const container = document.querySelector(CONTAINER_SELECTOR);
    if (!container) return;

    container.innerHTML = `
      <div style="padding: 2rem; background: #fff3cd; border: 1px solid #ffc107; border-radius: 8px; margin: 1rem 0;">
        <h3 style="margin: 0 0 0.5rem 0; color: #856404;">⚠️ Algolia Frequently Bought Together Debug</h3>
        <p style="margin: 0; color: #856404;">${message}</p>
      </div>
    `;
  }

  /**
   * Show loading message
   */
  function showLoading(message) {
    const container = document.querySelector(CONTAINER_SELECTOR);
    if (!container) return;

    const loading = container.querySelector('.algolia-loading');
    if (loading) {
      loading.innerHTML = `
        <div style="text-align: center; padding: 2rem;">
          <div class="loading-spinner" style="margin: 0 auto 1rem;">
            <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66" width="40" height="40">
              <circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
            </svg>
          </div>
          <p style="color: #666;">${message}</p>
        </div>
      `;
    }
  }

  /**
   * Fetch frequently bought together using standard Algolia Search
   * Finds products with same product_type or vendor as fallback
   */
  async function fetchFrequentlyBoughtTogether(objectID, indexName, config, apiKey) {
    try {
      showLoading('🔍 Finding frequently bought together products...');
      
      // First, get the current product to extract attributes
      const currentProductUrl = `https://${config.applicationId}-dsn.algolia.net/1/indexes/${indexName}/${objectID}`;
      
      const currentProductResponse = await fetch(currentProductUrl, {
        method: 'GET',
        headers: {
          'X-Algolia-Application-Id': config.applicationId,
          'X-Algolia-API-Key': apiKey,
        },
      });

      if (!currentProductResponse.ok) {
        const errorText = await currentProductResponse.text();
        console.warn('⚠️ Could not fetch current product:', errorText);
        showError(`Could not fetch product details. Error: ${currentProductResponse.status}`);
        return [];
      }

      const currentProduct = await currentProductResponse.json();

      // Build filters to find similar products
      let filters = `NOT objectID:${objectID}`; // Exclude current product

      // Try to filter by same product type first
      if (currentProduct.product_type) {
        filters += ` AND product_type:"${currentProduct.product_type}"`;
      } else if (currentProduct.vendor) {
        filters += ` AND vendor:"${currentProduct.vendor}"`;
      }

      // Search for related products
      const searchUrl = `https://${config.applicationId}-dsn.algolia.net/1/indexes/${indexName}/query`;

      const searchResponse = await fetch(searchUrl, {
        method: 'POST',
        headers: {
          'X-Algolia-Application-Id': config.applicationId,
          'X-Algolia-API-Key': apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: '',
          filters: filters,
          hitsPerPage: MAX_RECOMMENDATIONS,
          clickAnalytics: false, // Not a search query
        })
      });

      if (!searchResponse.ok) {
        const errorText = await searchResponse.text();
        console.error('❌ Search error:', errorText);
        showError(`Search failed (${searchResponse.status}): ${errorText}`);
        return [];
      }

      const searchData = await searchResponse.json();
      return searchData.hits || [];

    } catch (error) {
      console.error('❌ Error fetching frequently bought together:', error);
      showError(`Error: ${error.message}`);
      return [];
    }
  }
  
  function formatPriceForMarket(price) {
    if (price === undefined || price === null || price === "") return "";

    try {
      // Get currency from Shopify if available
      let currency = "EUR"; // default
      if (typeof window.Shopify !== "undefined" && window.Shopify.currency) {
        currency = window.Shopify.currency.active || window.Shopify.currency;
      } else {

        // Fallback: detect from URL
        const url = window.location.href.toLowerCase();
        if (url.includes("united-kingdom") || url.includes("/en-gb/")) {
          currency = "GBP";
        } else if (url.includes("hong-kong") || url.includes("/en-hk/")) {
          currency = "HKD";
        } else if (
          url.includes("switzerland") ||
          url.includes("/ch/") ||
          url.includes("-ch/")
        ) {
          currency = "CHF";
        } else if (url.includes("denmark")) {
          currency = "DKK";
        } else if (url.includes("sweden")) {
          currency = "SEK";
        }
      }

      // Get currency symbol
      const symbols = {
        EUR: "€",
        GBP: "£",
        HKD: "HK$",
        CHF: "CHF",
        DKK: "€",
        SEK: "€",
        USD: "$",
      };

      const symbol = symbols[currency] || "€";
      return `${symbol}${parseFloat(price).toFixed(2)}`;
    } catch (error) {
      // Fallback to EUR
      return `€${parseFloat(price).toFixed(2)}`;
    }
  }

  function getResizedImage(url, width = "100") {
    if (!url || !url.includes("cdn.shopify.com")) return url;

    try {
      // Rimuoviamo eventuali parametri esistenti (come v=...) per pulizia o li gestiamo
      // L'approccio più sicuro con Shopify è aggiungere i parametri alla fine
      const separator = url.includes("?") ? "&" : "?";

      // format=auto: Shopify serve AVIF o WebP in base al browser
      // crop=center: Assicura che l'immagine riempia il box se il ratio è diverso
      // height: Opzionale, se vuoi mantenere un ratio fisso, altrimenti lascia che width guidi
      return `${url}${separator}width=${width}&format=auto&crop=center`;
    } catch (e) {
      return url;
    }
  };

  function renderSimpleProductCard(hit, index, lang = window.Shopify.country, queryID) {
    // Use the main instance to access helper methods if provided
    const imageUrl =
      hit.image ||
      hit.images?.[0] ||
      "https://cdn.shopify.com/s/files/1/0652/6789/2395/files/blank_bottle.svg?v=1760432479";
    const productUrl = `${hit.handle}`;
    const productId = hit.id;
    const vendor = hit.vendor || "";
    const title = hit.title || "";
    
    // Check product type for Bundles/Accessories handling
    const productType = hit.product_type || "";
    const isAccessoryOrBundle = productType.includes("Accessori") || productType.toLowerCase().includes("bundles");
    const isFood = productType.includes("Food") || productType.includes("Alimentari");
    const useHdCenteredImage = isAccessoryOrBundle || isFood;

    // Use higher resolution for Bundles/Accessories/Food (800px instead of default)
    const imageWidth = useHdCenteredImage ? "800" : "533";

    // NEW APPROACH: Use market-specific index as PRIMARY index when available
    const baseIndexName = window.algoliaConfig?.indexName || 'shopify_products';
    const marketIndexName = getMarketSpecificIndex(baseIndexName);

    const availableEUQty = parseInt(hit.meta?.xtrawine?.["availability-eu"]) || 0;
    const availableHKQty = parseInt(hit.meta?.xtrawine?.["availability-hk"]) || 0;
    const availableQty = availableEUQty + availableHKQty;

    const preorderOrderedQty =
      parseInt(hit.meta?.xtrawine?.["preorderOrderedQty-eu"]) || 0;
    const preorderOrderedQtyAvailabilityDate =
      hit.meta?.xtrawine["preorderOrderedQtyAvailabilityDate-eu"] != null
        ? new Date(hit.meta?.xtrawine["preorderOrderedQtyAvailabilityDate-eu"])
        : null;

    // Get the appropriate market pricing field for this market
    // Use instance method if available (for consistency with other pricing logic), fallback to standalone
    const marketPricingField = getMarketPricingField();
    const marketPricingData = marketPricingField
      ? hit[marketPricingField]
      : null;

    // Use market-specific pricing if available, otherwise fall back to base price
    const currentMarketPrice = marketPricingData?.price || hit.price;
    const compareAtMarketPrice =
      marketPricingData?.compare_at_price || hit.compare_at_price;
    const price = currentMarketPrice
      ? formatPriceForMarket(currentMarketPrice)
      : "";

    const lowestPrice =
      compareAtMarketPrice &&
        parseFloat(compareAtMarketPrice) > parseFloat(currentMarketPrice)
        ? formatPriceForMarket(parseFloat(compareAtMarketPrice).toFixed(2))
        : "";

    // Recupera inventory policy per gestire preordine
    const inventoryPolicy = hit.inventory_policy;

    // Recupera titolo senza brand
    const fullTitle = hit.title;
    const vendorText = hit.vendor;

    let noBrandedTitle = fullTitle;

    if (vendorText) {
      // 1. Parole del vendor (split su spazi, trim punteggiatura; rispetta accenti)
      const vendorWords = vendorText
        .split(/\s+/)
        .map((w) => w.replace(/^[.,\-;:!?()\[\]{}'"]+|[.,\-;:!?()\[\]{}'"]+$/g, ""))
        .filter((w) => w.length > 0);
      const vendorSet = new Set(vendorWords.map((w) => w.toLowerCase()));

      // 2. Parole del titolo (stessa normalizzazione)
      const titleWords = fullTitle
        .split(/\s+/)
        .map((w) => w.replace(/^[.,\-;:!?()\[\]{}'"]+|[.,\-;:!?()\[\]{}'"]+$/g, ""))
        .filter((w) => w.length > 0);

      // 3. Quante parole iniziali del titolo sono tutte contenute nel vendor? (ordine qualsiasi)
      let n = 0;
      for (let i = 1; i <= titleWords.length; i++) {
        const prefix = titleWords.slice(0, i);
        if (prefix.every((w) => vendorSet.has(w.toLowerCase()))) n = i;
        else break;
      }

      if (n > 0) {
        // 4. Rimuovi le prime n parole dal titolo (costruendo il match sull’originale)
        const escapedFirstN = titleWords.slice(0, n).map((w) =>
          w.replace(/[-\/\\^$*+?.()|[\]{}]/g, "\\$&")
        );
        const pattern = `^${escapedFirstN.join("[\\s]+")}`;
        const regex = new RegExp(pattern, "i");
        noBrandedTitle = fullTitle.replace(regex, "").trim();
      }

      // 5. Pulizia finale
      noBrandedTitle = noBrandedTitle
        .replace(/\s{2,}/g, " ")
        .trim()
        .replace(/^[\s.,\-;:!?()\[\]{}'"]+|[\s.,\-;:!?()\[\]{}'"]+$/g, "");
    }

    // Use existing helper methods if available
    const alcoholPercentage = hit.meta?.xtrawine?.alcoholPercentage || "";

    // Extract awards using existing helper if available
    const awards = hit.meta?.xtrawine?.awardsListJson || [];

    // Recupera valore di lowest price corretto
    const marketReference = getMarketReference();
    const lowestPriceKeyReference = `omnibus_price-${marketReference}`;
    const lowestPriceMetafield = hit.meta?.xtrawine[lowestPriceKeyReference];
    const lowestPriceToCompare = parseFloat(lowestPriceMetafield).toFixed(2);

    // Gestione icona pulsante aggiunta al carrello rapida
    const buttonIcon = () => {
      if (marketIndexName.includes("hong_kong")) {
        if (availableHKQty > 0) {
          return `
          <span class="svg-cart-default">                                      
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 264.25 150.71"><path d="M104.12,0,258.91.61a5.37,5.37,0,0,1,5.22,6.49L243.8,101.83a5.36,5.36,0,0,1-5.24,4.24H120a5.36,5.36,0,0,1-5.25-4.25L94.55,6.6l-.06-.26L93.16.06" fill="#0a0b0a"></path><path d="M143.39,150.71a18.08,18.08,0,1,1,18.07-18.07A18.1,18.1,0,0,1,143.39,150.71Z" fill="#0a0b0a"></path><path d="M218,150.71a18.08,18.08,0,1,1,18.07-18.07A18.1,18.1,0,0,1,218,150.71Z" fill="#0a0b0a"></path><path d="M140.7,15H18.14a7.5,7.5,0,0,1,0-15H140.7a7.5,7.5,0,0,1,0,15Z" fill="#1d1d1b"></path><path d="M197.24,45H74.67a7.5,7.5,0,0,1,0-15H197.24a7.5,7.5,0,1,1,0,15Z" fill="#1d1d1b"></path><path d="M48.64,45H26.31a7.5,7.5,0,0,1,0-15H48.64a7.5,7.5,0,1,1,0,15Z" fill="#1d1d1b"></path><path d="M161.15,76.06H38.59a7.5,7.5,0,0,1,0-15H161.15a7.5,7.5,0,0,1,0,15Z" fill="#1d1d1b"></path><path d="M130.06,106.07H7.5a7.5,7.5,0,0,1,0-15H130.06a7.5,7.5,0,0,1,0,15Z" fill="#1d1d1b"></path></svg>
          </span>
          `;
        } else if (availableQty == 0) {
          return `
          <span class="svg-cart-bis">
            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px">
              <path d="M160-200v-80h80v-280q0-83 50-147.5T420-792v-28q0-25 17.5-42.5T480-880q25 0 42.5 17.5T540-820v28q80 20 130 84.5T720-560v280h80v80H160ZM480-80q-33 0-56.5-23.5T400-160h160q0 33-23.5 56.5T480-80Z"></path>
            </svg>
          </span>
          `;
        } else {
          return `
          <span class="svg-cart-default">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 283.4645669 283.4645669">
              <g fill="#1d1d1b">
                  <path d="M241.47 211.106H97.26a6.53 6.53 0 0 1-6.387-5.171L66.248 90.074a6.173 6.173 0 0 1-.07-.324l-6.823-32.1-45.473-27.486a6.528 6.528 0 1 1 6.753-11.175L68.53 47.937a6.527 6.527 0 0 1 3.01 4.23l6.349 29.873 188.343.746a6.529 6.529 0 0 1 6.358 7.899l-24.736 115.263a6.529 6.529 0 0 1-6.384 5.158ZM125.68 265.417c-12.125 0-21.988-9.864-21.988-21.988s9.863-21.988 21.988-21.988 21.988 9.863 21.988 21.988-9.864 21.988-21.988 21.988ZM216.437 265.417c-12.124 0-21.988-9.864-21.988-21.988s9.864-21.988 21.988-21.988 21.988 9.863 21.988 21.988-9.864 21.988-21.988 21.988Z"></path>
              </g>
            </svg>
          </span>
          `;
        }
      } else {
        if (availableEUQty <= 0 && preorderOrderedQty > 0) {
          return `
          <span class="svg-cart-preorder">
            <svg width="36" height="35" viewBox="0 0 36 35" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
            <rect width="36" height="35" fill="url(#pattern0_129_2924)"/>
            <defs>
            <pattern id="pattern0_129_2924" patternContentUnits="objectBoundingBox" width="1" height="1">
            <use xlink:href="#image0_129_2924" transform="matrix(0.00166667 0 0 0.00171429 0 -0.0142857)"/>
            </pattern>
            <image id="image0_129_2924" width="600" height="600" preserveAspectRatio="none" xlink:href="data:image/png;base64,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"/>
            </defs>
            </svg>
          </span>
          `;
        } else if (availableEUQty <= 0 && preorderOrderedQty <= 0) {
          return `
            <span class="svg-cart-bis">
              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px">
                <path d="M160-200v-80h80v-280q0-83 50-147.5T420-792v-28q0-25 17.5-42.5T480-880q25 0 42.5 17.5T540-820v28q80 20 130 84.5T720-560v280h80v80H160ZM480-80q-33 0-56.5-23.5T400-160h160q0 33-23.5 56.5T480-80Z"></path>
              </svg>
            </span>
          `;
        } else {
          return `
          <span class="svg-cart-default">
            ${window.discountCodes != null &&
              window.discountCodes.includes("XW-GROUP") &&
              !isOnSale
              ? `<span>%</span>`
              : ``
            }
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 283.4645669 283.4645669">
              <g fill="#1d1d1b">
                  <path d="M241.47 211.106H97.26a6.53 6.53 0 0 1-6.387-5.171L66.248 90.074a6.173 6.173 0 0 1-.07-.324l-6.823-32.1-45.473-27.486a6.528 6.528 0 1 1 6.753-11.175L68.53 47.937a6.527 6.527 0 0 1 3.01 4.23l6.349 29.873 188.343.746a6.529 6.529 0 0 1 6.358 7.899l-24.736 115.263a6.529 6.529 0 0 1-6.384 5.158ZM125.68 265.417c-12.125 0-21.988-9.864-21.988-21.988s9.863-21.988 21.988-21.988 21.988 9.863 21.988 21.988-9.864 21.988-21.988 21.988ZM216.437 265.417c-12.124 0-21.988-9.864-21.988-21.988s9.864-21.988 21.988-21.988 21.988 9.863 21.988 21.988-9.864 21.988-21.988 21.988Z"></path>
              </g>
            </svg>
          </span>
          `;
        }
      }
    };

    function buttonMessage(date = "") {
      const preorderByDate = new Intl.DateTimeFormat("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(date);

      const preorderByDateLabel = t("preorder_by").replace("{date}", preorderByDate);

      if (marketIndexName.includes("hong_kong")) {
        // --- Logica HONG KONG (invariata) ---
        if (availableHKQty > 0) {
          return {
            isVisible: true,
            text: t("express_shipping"),
          };
        } else if (availableQty == 0) {
          return {
            isVisible: true,
            text: t("get_notification"),
          };
        } else {
          return {
            isVisible: false,
            text: "",
          };
        }
      } else {
        // --- Logica NON-HK (Corretta) ---
        if (
          availableEUQty <= 0 && // <-- CORRETTO (da availableQty)
          preorderOrderedQty > 0 &&
          preorderOrderedQtyAvailabilityDate != null &&
          preorderOrderedQty > 0
        ) {
          return {
            isVisible: true,
            text: preorderByDateLabel,
          };
        } else if (availableEUQty <= 0 && preorderOrderedQty > 0) {
          // <-- CORRETTO (da availableQty)
          return {
            isVisible: true,
            text: t("preorder"),
          };
        } else if (availableEUQty <= 0 && preorderOrderedQty <= 0) {
          // <-- CORRETTO (da availableQty)
          return {
            isVisible: true,
            text: t("get_notification"),
          };
        } else {
          // Prodotto disponibile in EU
          return {
            isVisible: false,
            text: "",
          };
        }
      }
    }

    const buttonMessageObj = buttonMessage(preorderOrderedQtyAvailabilityDate);

    // Assegna icona premio corretta
    function renderAwardsList(awardsListJsonString) {
      const MAX_AWARDS_TO_SHOW = 3;

      if (!awardsListJsonString) {
        return "";
      }

      try {
        // 1. Pulizia e Parsing
        const cleanJsonString =
          awardsListJsonString.startsWith('"') &&
            awardsListJsonString.endsWith('"')
            ? awardsListJsonString.slice(1, -1)
            : awardsListJsonString;

        const data = JSON.parse(cleanJsonString);
        const awards = data.awardsListJson;

        if (!awards || awards.length === 0) {
          return "";
        }

        const limitedAwards = awards.slice(0, MAX_AWARDS_TO_SHOW);

        // 2. Iterazione e Markup: Crea l'HTML per ciascun premio.
        const awardsHtml = limitedAwards
          .map((award) => {
            let displayValue = award.prizeValue; // Default: mostra prizeValue
            let className = null;
            let iconStyle = 'style="display: none"'; // Default: icona nascosta
            let prizeValueText = award.prizeValue; // Testo da mostrare nello span .aw-score
            let prizeUnitText = "/100";

            const prizeImageParts = award.prizeImage
              ? award.prizeImage.split("_")
              : [];
            const lastPart =
              prizeImageParts.length > 0
                ? prizeImageParts[prizeImageParts.length - 1]
                : null;

            // Implementazione della logica Liquid per calcolare 'value' e 'class'
            switch (award.guideName) {
              case "Bibenda":
                className = "BB";
                // value = award.prizeImage | split: '_' | last | plus: 1
                if (lastPart) {
                  prizeValueText = parseInt(lastPart) + 1;
                } else {
                  prizeValueText = null;
                }
                iconStyle = 'style="display: block"';
                prizeUnitText = t("award_grapes_label");
                break;

              case "Gambero Rosso":
                className = "GR";
                // Logica di mappatura basata su prizeImage
                switch (award.prizeImage) {
                  case "SpriteProduct pic_GamberoNero_4":
                    prizeValueText = 1;
                    break;
                  case "SpriteProduct pic_GamberoNero_3":
                  case "SpriteProduct pic_GamberoNero_2":
                    prizeValueText = 2;
                    break;
                  case "SpriteProduct pic_GamberoNero_1":
                    prizeValueText = 3;
                    break;
                  default:
                    prizeValueText = null;
                    break;
                }
                iconStyle = 'style="display: block"';
                prizeUnitText = t("award_glasses_label");
                break;

              case "Veronelli":
                className = "VR";
                // Logica di mappatura basata su prizeImage
                switch (award.prizeImage) {
                  case "SpriteProduct pic_Veronelli_1":
                    prizeValueText = 1;
                    break;
                  case "SpriteProduct pic_Veronelli_2":
                    prizeValueText = 2;
                    break;
                  case "SpriteProduct pic_Veronelli_3":
                  case "SpriteProduct pic_Veronelli_4":
                    prizeValueText = 3;
                    break;
                  default:
                    prizeValueText = null;
                    break;
                }
                iconStyle = 'style="display: block"';
                prizeUnitText = t("award_stars_label");
                break;

              case "Hachette":
                className = "HC";
                // Logica di mappatura basata su prizeImage
                switch (award.prizeImage) {
                  case "SpriteProduct pic_Hachette_2":
                    prizeValueText = 2;
                    break;
                  case "SpriteProduct pic_Hachette_3":
                    prizeValueText = 3;
                    break;
                  default:
                    prizeValueText = null;
                    break;
                }
                iconStyle = 'style="display: block"';
                prizeUnitText = t("award_stars_label");
                break;

              case "Vitae AIS":
                className = "VT";
                // value = award.prizeImage | split: '_' | last
                if (lastPart) {
                  prizeValueText = lastPart;
                } else {
                  prizeValueText = null;
                }
                iconStyle = 'style="display: block"';
                break;

              case "Slow Wine":
                className = "SL";
                prizeValueText = null; // Il Liquid non imposta un 'value' esplicito
                iconStyle = 'style="display: block"';
                break;

              // Altri casi (Decanter, James Suckling, ecc.) non hanno una logica di 'value' diversa dal default
              case "Decanter":
                className = "DE";
                iconStyle = 'style="display: none"';
                break;
              case "James Suckling":
                className = "JS";
                iconStyle = 'style="display: none"';
                break;
              case "Luca Maroni":
                className = "LM";
                iconStyle = 'style="display: none"';
                break;
              case "Robert Parker":
                className = "RP";
                iconStyle = 'style="display: none"';
                break;
              case "Vinous":
                className = "VO";
                iconStyle = 'style="display: none"';
                break;
              case "Wine Spectator":
                className = "WS";
                iconStyle = 'style="display: none"';
                break;
              case "Xtrawine":
                className = "XW";
                iconStyle = 'style="display: none"';
                break;
              case "Hachette":
                className = "HC";
                iconStyle = 'style="display: none"';
                break;

              default:
                // Per tutti gli altri, usiamo prizeValue e nessuna classe specifica (o solo la guidaName)
                className = award.guideName;
                prizeValueText = award.prizeValue;
                iconStyle = 'style="display: none"';
                break;
            }

            // Se non è stato impostato un valore specifico, torniamo a usare il prizeValue originale
            if (prizeValueText === null) {
              prizeValueText = award.prizeValue;
            }

            prizeUnitText = prizeValueText + prizeUnitText;

            // 3. Creazione dell'HTML
            return `
                <div class="d-flex align-items-center tooltip-trigger">
                    <span class="rounded-circle d-flex justify-content-center align-items-center border border-1 text-center">
                        <span class="aw-value d-flex text-center">
                            <span class="aw-score">${prizeValueText}</span>
                            <span class="aw-icon ${className}" ${iconStyle}></span>
                        </span>
                    </span>
                    <span class="fw-bold aw-name">${className || award.guideName
              }</span>
                    <div class="tooltip-container">
                        <span style="font-weight: 700">${t(`awarded_by_label`).replace("{guide}", award.guideName).replace("{vote}", prizeUnitText)}</span><br>
                        <span>${t(`awards_description.${className}`,)}</span>
                    </div>
                </div>
                `;
          })
          .join(""); // Unisci tutti i blocchi HTML dei premi

        // 4. Contenitore della Lista
        return `<div class="aa-awards-list">${awardsHtml}</div>`;
      } catch (error) {
        console.error("Errore nel parsing della lista premi:", error);
        return ""; // In caso di errore, non renderizzare nulla
      }
    }

    // Existing compare price logic
    const hasComparePrice =
      compareAtMarketPrice &&
      parseFloat(compareAtMarketPrice) > parseFloat(currentMarketPrice);
    const comparePrice = hasComparePrice
      ? parseFloat(compareAtMarketPrice).toFixed(2) // Usa il prezzo di listino del mercato
      : null;
    const isOnSale = hasComparePrice;

    // FIX OMNIBUS: la percentuale va calcolata sul prezzo più basso (30gg), quando
    // disponibile, non sul prezzo di listino pieno - mirror della logica gia' corretta
    // in PDP (snippets/product-media-gallery.liquid).
    const hasValidOmnibusPrice =
      lowestPriceMetafield !== undefined &&
      lowestPriceMetafield !== null &&
      !isNaN(parseFloat(lowestPriceMetafield));
    const discountBasePrice = hasValidOmnibusPrice
      ? parseFloat(lowestPriceMetafield)
      : parseFloat(compareAtMarketPrice);

    const discountPercent = isOnSale
      ? Math.round(
        ((discountBasePrice - parseFloat(currentMarketPrice)) /
          discountBasePrice) *
        100,
      )
      : 0;

    // FIX: LOGICA VISUALIZZAZIONE FASCETTA SCONTO (OMNIBUS)
    let showDiscountBadge = isOnSale && discountPercent > 0;

    // Se esiste il Lowest Price (Omnibus) e il prezzo attuale non è inferiore ad esso,
    // significa che la differenza è 0 o negativa rispetto allo storico, quindi nascondiamo la fascetta.
    if (showDiscountBadge && lowestPriceMetafield && !isNaN(parseFloat(lowestPriceMetafield))) {
      const floatLowest = parseFloat(lowestPriceMetafield);
      const floatCurrent = parseFloat(currentMarketPrice);

      // Tolleranza minima per errori di virgola mobile, ma concettualmente:
      // Se Prezzo Attuale >= Prezzo Più Basso, nascondi badge.
      if (floatCurrent >= floatLowest) {
        showDiscountBadge = false;
      }
    }

    const isAddToCartDisabled = (() => {
      if (marketIndexName.includes("hong_kong")) {
        // Logica HK: si basa sulla disponibilità totale (come l'originale)
        if (availableQty <= 0) {
          return "disabled";
        }
      } else {
        // Logica NON-HK: si basa SOLO su EU e Preorder
        if (availableEUQty <= 0 && preorderOrderedQty <= 0) {
          return "disabled"; // Disabilita se EU è 0 E non c'è preorder
        }
      } // In tutti gli altri casi (HK disponibile, o EU disponibile, o Preorder attivo)
      return ""; // Abilitato
    })();

    const qtyForDisplay = marketIndexName.includes("hong_kong")
      ? availableQty
      : availableEUQty;

    // Consideriamo i primi 4 prodotti (desktop) come "Above the Fold"
    const isAboveTheFold = index < 4;

    const loadingAttr = isAboveTheFold ? "eager" : "lazy";
    const fetchPriorityAttr = isAboveTheFold
      ? 'fetchpriority="high"'
      : 'fetchpriority="auto"';

    return `
      <li class="grid__item csr" 
          data-product-id="${productId}"
          data-algolia-object-id="${hit.objectID}"
          data-algolia-position="${index}"
          data-algolia-query-id="${queryID || ''}">
        <div class="card-wrapper product-card-wrapper font-fira">
          <div class="card card--standard card--media" style="--ratio-percent: 125.0%;">
            <a title="${noBrandedTitle}" href="${productUrl}" id="AbsoluteCardLink--${productId}" class="position-absolute top-0 w-100 h-100 d-block" style="left:0; z-index: 1;"></a>

            <div class="card__inner color-scheme-4 ratio " style="--ratio-percent: 125.0%;">
              <div class="card__media">
                <div class="media media--transparent media--hover-effect"${useHdCenteredImage ? ` style="transform:translate(-50%);${isFood ? 'width:70%;' : ''}"` : ''}>
                  <img
                    srcset="
                      ${getResizedImage(imageUrl, useHdCenteredImage ? "800" : "165")} 165w,
                      ${getResizedImage(imageUrl, useHdCenteredImage ? "800" : "360")} 360w,
                      ${getResizedImage(imageUrl, imageWidth)} 533w,
                      ${getResizedImage(imageUrl, useHdCenteredImage ? "800" : "720")} 720w,
                      ${getResizedImage(imageUrl, useHdCenteredImage ? "800" : "940")} 940w
                    "
                    src="${getResizedImage(imageUrl, imageWidth)}"
                    sizes="(min-width: 1200px) 267px, (min-width: 990px) 150px, (min-width: 768px) 210px, (min-width: 575px) 145px, 140px"
                    alt="${title}"
                    class="motion-reduce"
                    loading="${loadingAttr}"
                    ${fetchPriorityAttr}
                    width="533"
                    height="666" 
                    style="object-fit: contain;"
                  >
                </div>
              </div>
              <div class="card__content d-flex w-50 align-items-end">
                <div class="card__information card__details d-flex flex-column gap-4">
                  ${awards.length > 0
        ? `
                  <div class="awards d-flex flex-column gap-3">
                    ${renderAwardsList(awards)}
                  </div>`
        : ""
      }
                              
                  ${alcoholPercentage
        ? `
                    <div class="alcohol-percent">
                      <svg width="20" height="28" viewBox="0 0 20 28" fill="none"
                          xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                          <rect width="20" height="28" fill="url(#pattern0_4002_476)"></rect>
                          <defs>
                              <pattern id="pattern0_4002_476" patternContentUnits="objectBoundingBox" width="1"
                                  height="1">
                                  <use xlink:href="#image0_4002_476" transform="matrix(0.0035 0 0 0.0025 -0.2 0)">
                                  </use>
                              </pattern>
                              <image id="image0_4002_476" width="400" height="400"
                                  xlink:href="data:image/png;base64,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">
                              </image>
                          </defs>
                      </svg>
                      <span>${alcoholPercentage}%</span>
                    </div>
                  `
        : ""
      }
                </div>
                ${showDiscountBadge
        ? `
                <span class="discount-badge discount-badge-xtrawine">
                  <p id="discount-text"><strong>-${discountPercent}%</strong></p>
                </span>`
        : ""
      }
              </div>
              <wishlist-button aria-label="Add To Wishlist" data-with-epi="true" class="swym-button product_${hit.id
      } swym-icon swym-heart swym-loaded wishlist-button" data-swaction="addToWishlist" data-product-id="${hit.id
      }" data-variant-id="${hit.objectID
      }" data-product-url="https://www.xtrawine.com${productUrl}" data-product-title="${title}" data-wishlisted-success="${t(
        "wishlisted_success",
      )}" data-unwishlisted-success="${t(
        "unwishlisted_success",
      )}" data-product-img="${imageUrl}" style="display: block;">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 283.46 283.46" class="icon-heart">
                    <path d="M141.67,259.14l-4.14-3.07c-26.17-19.42-62-46-88.89-77.27C16.16,141,5.06,105.21,15.65,72.35c8-24.76,34.84-45.75,61.13-47.79h0c26.4-2.06,48.67,9.84,64.77,34.47,21.2-33,53-38.44,76.66-32.08a72,72,0,0,1,53.45,72.13c-1.9,60.74-73.77,116.46-121.33,153.33ZM77.5,33.77c-22.45,1.75-46.25,20.33-53,41.42-9.67,30,.54,61.92,31.2,97.59,25.7,29.88,60.21,55.69,85.89,74.75l3.12-2.42c43.39-33.64,116-89.93,117.76-146.32,1.07-34.33-23.41-56.67-46.61-62.91-33.26-8.94-58,11.89-70.14,34.36l-4,7.42-4.11-7.35c-10.07-18-28.65-39-60.07-36.54Z" fill="#0a0b0a" stroke="#0a0b0a" stroke-miterlimit="10" stroke-width="5"></path>
                </svg>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 283.46 283.46" class="icon-heart-full">
                    <path d="M218.21,27c-23.67-6.36-55.46-.89-76.66,32.08C125.45,34.39,103.2,22.5,76.78,24.56s-53.15,23-61.13,47.79c-10.59,32.85.51,68.67,33,106.45,26.87,31.25,62.72,57.85,88.89,77.27l4.14,3.07,8.66-6.73c47.56-36.87,119.43-92.59,121.33-153.34A72,72,0,0,0,218.21,27Z" fill="#1d1d1b"></path>
                </svg>
                <span class="swym-tooltip swym-inject swym-tooltip-clickable">
                    <span class="swym-tooltip-text">Add to wishlist</span>
                </span>
              </wishlist-button>
            </div>
            <div class="position-static card__content  with-quick-add">
              <div class="card__information">
                <div class="d-flex flex-column gap-1 flex-grow-1">
                  <div class="grid-product-vendor h4">${vendor !== 'Xtrawine' ? vendor : ''}</div>
                  <div class="grid-product-title h3" id="title--${productId}">
                    <a title="${noBrandedTitle}" href="${productUrl}" id="CardLink--${productId}" class="full-unstyled-link" aria-labelledby="CardLink--${productId} Badge--${productId}">${noBrandedTitle}</a>
                  </div>
                </div>
                <div class="card-information">
                  <span class="available-qty-warning cart-product-availability 
                  ${qtyForDisplay >= 1 && qtyForDisplay <= 5 ? "low-stock" : ""
      } 
                  ${qtyForDisplay <= 0 ? "empty-stock" : ""}" 
                  data-product-id="${productId}">
                  <strong>
                  ${
      // CASO 1: Pochi prodotti rimasti (2-5)
      qtyForDisplay >= 2 && qtyForDisplay <= 5
        ? t("remaining_products", { count: qtyForDisplay })
        : ""
      }
                  ${
      // CASO 2: Unico prodotto rimasto
      qtyForDisplay == 1 ? t("remaining_product") : ""
      }
                  ${
      // CASO 2: Prodotto esaurito ma disponibile in Preordine (Solo NON-HK)
      !marketIndexName.includes("hong_kong") &&
        qtyForDisplay <= 0 &&
        preorderOrderedQty > 0
        ? t("preorder")
        : ""
      }
                  ${
      // CASO 3: Prodotto completamente esaurito (No Stock e No Preorder)
      qtyForDisplay <= 0 && preorderOrderedQty == 0
        ? t("no_remaining_products")
        : ""
      }
                  </strong></span>
                    <div class="col-12 col-md-12 price grid-product-price">
                      <div class="price__container">
                        <div class="price__regular">
                          <span class="visually-hidden visually-hidden--inline">Regular price</span>
                          <span class="price-item price-item--regular" id="price">
                            <span class="notranslate ht-money">${price}</span>
                          </span>
                          ${isOnSale
        ? `
                          <tooltip-component class="tooltip-trigger">
                              ${comparePrice !== lowestPriceToCompare
          ? `<s id="price-disc" class="price-item price-item--regular">
                                     ${formatPriceForMarket(comparePrice)}
                                </s>`
          : ""
        }
                              <div class="tooltip-container">
                                  <span style="font-weight: 700">
                                      ${t("list_price_strong")}
                                  </span>
                                  <span>
                                      ${t("list_price_text")}
                                  </span>
                              </div>
                          </tooltip-component>`
        : ""
      }
                        </div>
                      </div>
                      ${isOnSale && lowestPriceMetafield !== undefined
        ? `
                      <div id="sniffie-omnibus-price">
                        <tooltip-component class="sniffie-omnibus-price__label tooltip-trigger">
                          ${t("lowest_price_strong")}&nbsp; 
                          <span class="metafield-money" style="text-decoration: line-through;">${formatPriceForMarket(lowestPriceMetafield)}</span>
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 283.4645669 283.4645669"><path fill="#010101" d="M260.31 85.524c-6.483-15.324-15.76-29.087-27.578-40.904-11.817-11.817-25.579-21.094-40.903-27.577-15.871-6.713-32.726-10.116-50.097-10.116s-34.225 3.403-50.097 10.116C76.311 23.526 62.55 32.803 50.732 44.62 38.915 56.437 29.638 70.2 23.155 85.524 16.442 101.395 13.04 118.25 13.04 135.62s3.403 34.226 10.116 50.097c6.285 14.855 15.195 28.244 26.498 39.813l-20.975 51.008 58.805-24.184a127.45 127.45 0 0 0 4.152 1.844c15.872 6.713 32.727 10.116 50.097 10.116s34.226-3.403 50.097-10.116c15.324-6.483 29.086-15.76 40.903-27.578 11.817-11.817 21.095-25.579 27.577-40.903 6.713-15.871 10.117-32.726 10.117-50.097s-3.404-34.225-10.117-50.096ZM141.731 69.576c8.33 0 15.084 6.754 15.084 15.084 0 8.331-6.754 15.083-15.084 15.083S126.65 92.991 126.65 84.66c0-8.33 6.754-15.084 15.083-15.084Zm28.076 132.089h-56.151v-17.977h21.028V121.25h-18.677v-17.974h36.652v80.412h17.148v17.977Z"></path></svg>
                          <div class="tooltip-container">
                            <span style="font-weight: 700">
                                ${t("lowest_price")}:
                            </span>
                            <span>
                                ${t("lowest_price_text")}
                            <span>
                          </div>
                        </tooltip-component>
                      </div>`
        : ``
      }
                    </div>     
                  </div>
                </div>
                <!--
                <div class="triangular-container">
                    <button class="triangular-button ${isAddToCartDisabled}" onclick="addToCart('${productId}', '${hit.handle
      }', ${availableQty}, ${availableEUQty}, ${availableHKQty}, ${preorderOrderedQty}, '${preorderOrderedQtyAvailabilityDate}', '${marketIndexName}', '${productUrl}', event)" type="button" aria-label="${t(
        "add_to_cart",
      )}">
                        <span class="icon">${buttonIcon()}</span> </button>
                </div>
                -->
                <div class="quick-add ${buttonMessageObj.isVisible ? "has-message" : ""
      } no-js-hidden">
                  <div class="quick-add__submit">
                    <div class="button-add-to-cart-wrapper font-lato">
                    <div class="triangle-bottom-right-text-wrapper">
                      ${buttonMessageObj.text}
                    </div>
                    <button class="button button--full-width triangle-bottom-right d-flex add-to-cart-btn-xtrawine ${isAddToCartDisabled}" onclick="addToCart('${productId}', '${hit.handle
      }', ${availableQty}, ${availableEUQty}, ${availableHKQty}, ${preorderOrderedQty}, '${preorderOrderedQtyAvailabilityDate}', '${marketIndexName}', '${productUrl}', event, '${queryID || ''}', '${hit.objectID}')" type="button" aria-label="${t(
        "add_to_cart",
      )}">
                      <span class="add-to-cart-icon-wrapper default-icon">
                        ${buttonIcon()}
                      </span>
                      <span class="sold-out-message hidden" style="display: none;">Sold out</span>
                      <div class="loading__spinner hidden">
                        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                        </svg>
                      </div>
                    </button>
                    </div>
                  </div>
                </div>
                <div class="card__badge bottom left"></div>
              </div>
            </div>
          </div>
        </div>
      </li>
    `;
  };

  /**
   * Format price for current market
   */
  function formatPrice(price) {
    if (!price) return '';
    const currency = window.Shopify?.currency?.active || 'EUR';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
      }).format(price);
    } catch (e) {
      return `${currency} ${parseFloat(price).toFixed(2)}`;
    }
  }

  /**
   * Render frequently bought together products
   */
  function renderFrequentlyBoughtTogether(hits, lang, queryID) {
    const container = document.querySelector(CONTAINER_SELECTOR);
    if (!container) return;

    // Rimuovi loading spinner
    const loading = container.querySelector('.algolia-loading');
    if (loading) {
      loading.remove();
    }
  
    if (hits.length === 0) {
      container.style.display = 'none';
      return;
    }
  
    // Mostra i bottoni dello slider ora che abbiamo i risultati
    const sliderButtons = container.querySelector('.slider-buttons');
    if (sliderButtons) sliderButtons.style.display = 'block';

    // Create grid container
    const grid = document.createElement('ul');
    grid.className = 'algolia-frequently-bought-together-grid grid product-grid contains-card contains-card--product contains-card--standard grid--4-col-desktop grid--1-col-tablet-down slider slider--desktop slider--tablet grid--peek';
    grid.id = 'Slider-algolia-frequently-bought-together'; // Fondamentale per: this.querySelector('[id^="Slider-"]')

    // Render each product
    hits.forEach((hit, index) => {
      const cardHTML = renderSimpleProductCard(hit, index, lang, queryID);
      const wrapper = document.createElement('div');
      wrapper.innerHTML = cardHTML;
      const card = wrapper.firstElementChild;
  
      // ASSEGNA L'ID RICHIESTO DAL COMPONENTE ALLE SINGOLE SLIDE
      card.id = `Slide-algolia-${index}`; // Fondamentale per: this.querySelectorAll('[id^="Slide-"]')
  
      // Logica Load More (Vedi Nota UX sotto)
      if (index >= INITIAL_VISIBLE) {
        card.style.display = 'none';
        card.classList.add('algolia-hidden-card');
      }
  
      grid.appendChild(card);
    });
  
    container.appendChild(grid);

    // Add Load More button if there are hidden products
    if (hits.length > INITIAL_VISIBLE) {
      const loadMoreBtn = document.createElement('button');
      loadMoreBtn.className = 'algolia-load-more-btn';
      loadMoreBtn.textContent = 'Load More';
      loadMoreBtn.type = 'button';
      
      loadMoreBtn.addEventListener('click', function() {
        const hiddenCards = grid.querySelectorAll('.algolia-hidden-card');
        const currentVisible = parseInt(grid.dataset.visibleCount) || INITIAL_VISIBLE;
        const cardsToShow = Math.min(LOAD_MORE_INCREMENT, hiddenCards.length);
        
        // Show next batch of cards
        for (let i = 0; i < cardsToShow && i < hiddenCards.length; i++) {
          hiddenCards[i].style.display = 'block';
          hiddenCards[i].classList.remove('algolia-hidden-card');
        }
        
        // Update visible count
        const newVisibleCount = currentVisible + cardsToShow;
        grid.dataset.visibleCount = newVisibleCount;
        
        // Hide button if all cards are now visible
        if (grid.querySelectorAll('.algolia-hidden-card').length === 0) {
          loadMoreBtn.style.display = 'none';
        }
      });
      
      container.appendChild(loadMoreBtn);
    }

    // Attach click listeners after render
    setTimeout(() => {
      attachClickListenersToFrequentlyBoughtTogether();
      
      // Forza il web component a rileggere il DOM e agganciare gli eventi
      if (typeof container.initSlider === 'function') {
        container.initSlider();
      }
    }, 100);
    
    // Send view event for impressions
    sendViewedEvent(hits, queryID);
  }

  /**
   * Initialize Frequently Bought Together using InstantSearch.js widget
   */
  async function initFrequentlyBoughtTogether() {
    const container = document.querySelector(CONTAINER_SELECTOR);
    if (!container) {
      return;
    }

    // Get current product objectID
    const objectID = getCurrentProductObjectID();
    if (!objectID) {
      showError('Cannot find product ID on this page. Make sure you are on a product page.');
      return;
    }

    // Get Algolia config
    const config = window.algoliaConfig;
    if (!config) {
      showError('Algolia configuration not found. Make sure algolia-config.liquid is loaded.');
      return;
    }

    // Support both apiKey and searchApiKey (for compatibility)
    const apiKey = config.searchApiKey || config.apiKey;
    
    if (!config.applicationId || !apiKey) {
      showError(`Algolia credentials missing. AppId: ${config.applicationId ? '✓' : '✗'}, ApiKey: ${apiKey ? '✓' : '✗'}`);
      return;
    }

    // Get dynamic index name (market-specific)
    const baseIndexName = config.indexName || 'shopify_products';
    const indexName = getMarketSpecificIndex(baseIndexName);
    
    if (!indexName) {
      showError('Could not determine Algolia index name');
      return;
    }

    // Check if InstantSearch is available
    if (!window.instantsearch) {
      showError('InstantSearch.js not loaded. Make sure it is included in your theme.');
      return;
    }

    // Check if Algolia Recommend client is available
    const recommendClient = window.recommendjs || window.AlgoliaRecommend || window['@algolia/recommend'];
    
    if (recommendClient && typeof recommendClient === 'function') {
      return initWithRecommend(objectID, indexName, config, apiKey, recommendClient);
    }

    // Check if frequentlyBoughtTogether widget is available
    if (window.instantsearch && window.instantsearch.widgets && window.instantsearch.widgets.frequentlyBoughtTogether) {
      return initWithWidget(objectID, indexName, config, apiKey);
    }
    // Fall back to search-based approach
    return initWithSearch(objectID, indexName, config, apiKey);
  }

  /**
   * Initialize with Algolia Recommend client
   */
  async function initWithRecommend(objectID, indexName, config, apiKey, recommendClientFactory) {
    showLoading('Loading frequently bought together from Algolia...');
    
    try {
      const recommendClient = recommendClientFactory(config.applicationId, apiKey);
      const productID = getShopifyProductID();
      
      // Use threshold parameter to get recommendations
      const recommendParams = {
        indexName: indexName,
        objectID: objectID,
        maxRecommendations: MAX_RECOMMENDATIONS,
      };
      
      // If we have product ID, add it as a model parameter
      if (productID) {
        recommendParams.model = 'bought-together';
        recommendParams.threshold = 0;
      }
      
      const results = await recommendClient.getFrequentlyBoughtTogether([recommendParams]);
      
      const hits = results.results?.[0]?.hits || [];
      const queryID = results.results?.[0]?.queryID;
      
      if (hits.length > 0) {
        renderFrequentlyBoughtTogether(hits, getCurrentLanguage(), queryID);
      } else {
        // No FBT data for this market/product: hide the entire section silently
        const container = document.querySelector(CONTAINER_SELECTOR);
        if (container) {
          const section = container.closest('section') || container.parentElement;
          if (section) section.style.display = 'none';
          else container.style.display = 'none';
        }
      }
    } catch (error) {
      // On error, hide the section silently rather than showing broken content
      const container = document.querySelector(CONTAINER_SELECTOR);
      if (container) {
        const section = container.closest('section') || container.parentElement;
        if (section) section.style.display = 'none';
        else container.style.display = 'none';
      }
    }
  }

  /**
   * Initialize with InstantSearch widget
   */
  async function initWithWidget(objectID, indexName, config, apiKey) {
    showLoading('Loading frequently bought together from Algolia...');

    try {
      // Initialize Algolia search client
      const searchClient = window.algoliasearch(
        config.applicationId,
        apiKey
      );

      // Create InstantSearch instance for Frequently Bought Together
      const search = window.instantsearch({
        indexName: indexName,
        searchClient: searchClient,
      });

      // Add frequentlyBoughtTogether widget
      search.addWidgets([
        window.instantsearch.widgets.frequentlyBoughtTogether({
          container: CONTAINER_SELECTOR,
          objectIDs: [objectID],
          maxRecommendations: MAX_RECOMMENDATIONS,
          templates: {
            item(hit, { html }) {
              return renderSimpleProductCard(hit, 0);
            },
            empty() {
              return '<div style="padding: 2rem; text-align: center; color: #666;">No frequently bought together products found.</div>';
            },
          },
        }),
      ]);

      // Start InstantSearch
      search.start();

      // Remove loading spinner after a delay
      setTimeout(() => {
        const loading = document.querySelector(CONTAINER_SELECTOR + ' .algolia-loading');
        if (loading) loading.remove();
      }, 1000);

    } catch (error) {
      initWithSearch(objectID, indexName, config, apiKey);
    }
  }

  /**
   * Fallback: Initialize with search-based recommendations
   */
  async function initWithSearch(objectID, indexName, config, apiKey) {
    const lang = getCurrentLanguage();
    const hits = await fetchFrequentlyBoughtTogether(objectID, indexName, config, apiKey);
    
    if (hits && hits.length > 0) {
      renderFrequentlyBoughtTogether(hits, lang);
    } else {
      // No results: hide the section silently
      const container = document.querySelector(CONTAINER_SELECTOR);
      if (container) {
        const section = container.closest('section') || container.parentElement;
        if (section) section.style.display = 'none';
        else container.style.display = 'none';
      }
    }
  }

  /**
   * Get or generate a consistent user token for analytics
   * Uses existing token from config or localStorage, or generates a new one
   */
  function getConsistentUserToken() {
    const config = window.algoliaConfig;
    
    // Priority 1: Use token from algolia config
    if (config?.userToken) {
      return config.userToken;
    }
    
    // Priority 2: Check localStorage
    try {
      const stored = JSON.parse(localStorage.getItem('algolia_config') || '{}');
      if (stored.userToken) {
        return stored.userToken;
      }
    } catch (e) { /* ignore */ }
    
    // Priority 3: Generate new token and save it
    const newToken = 'anonymous-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    try {
      const stored = JSON.parse(localStorage.getItem('algolia_config') || '{}');
      stored.userToken = newToken;
      localStorage.setItem('algolia_config', JSON.stringify(stored));
    } catch (e) { /* ignore */ }
    
    return newToken;
  }

  /**
   * Send a click event directly to Algolia Insights API.
   * Used on PSP where algolia-collection.js (and its sendClickEventToAlgolia) is not loaded.
   */
  async function sendInsightClickEvent(objectID, queryID, position) {
    const config = window.algoliaConfig;
    if (!config) return;
    const appId = config.applicationId || config.appId;
    const apiKey = config.searchApiKey || config.apiKey;
    if (!appId || !apiKey) return;

    const indexName = getMarketSpecificIndex(config.indexName || 'shopify_products');
    const userToken = getConsistentUserToken();

    const eventData = {
      eventType: 'click',
      eventName: 'Recommend Result Clicked',
      index: indexName,
      userToken: userToken,
      objectIDs: [String(objectID)],
      timestamp: Date.now()
    };
    if (queryID) {
      eventData.queryID = queryID;
      eventData.positions = [position || 1];
    }

    try {
      await fetch('https://insights.algolia.io/1/events', {
        method: 'POST',
        headers: {
          'X-Algolia-Application-Id': appId,
          'X-Algolia-API-Key': apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ events: [eventData] })
      });
    } catch (e) { 
      // Event failed silently
    }
  }

  /**
   * Send a view/impression event to Algolia Insights for Recommend widgets
   */
  async function sendViewedEvent(hits, queryID) {
    const config = window.algoliaConfig;
    if (!config) return;
    const appId = config.applicationId || config.appId;
    const apiKey = config.searchApiKey || config.apiKey;
    if (!appId || !apiKey) return;

    const indexName = getMarketSpecificIndex(config.indexName || 'shopify_products');
    const userToken = getConsistentUserToken();
    const objectIDs = hits.slice(0, 20).map(h => String(h.objectID)).filter(Boolean);
    if (objectIDs.length === 0) return;

    const eventData = {
      eventType: 'view',
      eventName: 'Frequently Bought Together Viewed',
      index: indexName,
      userToken: userToken,
      objectIDs: objectIDs,
      timestamp: Date.now()
    };
    
    // Include queryID for Recommend widgets
    if (queryID) {
      eventData.queryID = queryID;
    }

    try {
      await fetch('https://insights.algolia.io/1/events', {
        method: 'POST',
        headers: {
          'X-Algolia-Application-Id': appId,
          'X-Algolia-API-Key': apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ events: [eventData] })
      });
    } catch (e) { 
      // View event failed silently
    }
  }

  /**
   * Attach click listeners to frequently bought together products for Algolia analytics
   */
  function attachClickListenersToFrequentlyBoughtTogether() {
    // Get all product cards in frequently bought together section
    const container = document.querySelector(CONTAINER_SELECTOR);
    if (!container) return;

    // Instead of searching by href, find cards and attach to their links
    const cards = container.querySelectorAll('li.grid__item[data-product-id]');
    
    cards.forEach(card => {
      // Find the main product link in this card
      const productLink = card.querySelector('a[id*="CardLink"], a.full-unstyled-link');
      if (!productLink) return;

      productLink.addEventListener('click', function(e) {
        const algoliaObjectId = card.dataset.algoliaObjectId;
        if (!algoliaObjectId) return;
        const queryID = card.dataset.algoliaQueryId || null;
        const position = parseInt(card.dataset.algoliaPosition || '0') + 1;

        // On collection page: use shared function; on PSP: use local fallback
        if (typeof window.sendClickEventToAlgolia === 'function') {
          window.sendClickEventToAlgolia(algoliaObjectId, position, queryID);
        } else {
          sendInsightClickEvent(algoliaObjectId, queryID, position);
        }
      });
    });
  }

  /**
   * Wait for dependencies and initialize
   */
  function waitForDependencies(retryCount = 0) {
    // Check if required dependencies are loaded
    if (
      window.algoliaConfig &&
      window.Shopify &&
      window.algoliasearch &&
      window.AlgoliaTranslations
    ) {
      // Update algolia_config in localStorage with the correct market-specific index.
      // On PSP, algolia-collection.js does not run, so localStorage may have a stale
      // or base index. The web pixel reads indexName from here for Add To Cart / Purchase.
      try {
        const config = window.algoliaConfig;
        const marketIndex = getMarketSpecificIndex(config.indexName || 'shopify_products');
        const stored = JSON.parse(localStorage.getItem('algolia_config') || '{}');
        stored.indexName = marketIndex;
        stored.applicationId = stored.applicationId || config.applicationId || config.appId;
        stored.searchApiKey = stored.searchApiKey || config.searchApiKey || config.apiKey;
        stored.userToken = stored.userToken || config.userToken;
        localStorage.setItem('algolia_config', JSON.stringify(stored));
      } catch (e) { /* silent */ }

      // All dependencies ready
      initFrequentlyBoughtTogether();
    } else if (retryCount < MAX_RETRIES) {
      // Retry after delay
      setTimeout(() => {
        waitForDependencies(retryCount + 1);
      }, RETRY_DELAY);
    } else {
      // Max retries reached
      console.error('❌ Algolia dependencies not loaded after', MAX_RETRIES, 'retries');
      showError('Failed to load Algolia dependencies. Please refresh the page.');
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForDependencies);
  } else {
    waitForDependencies();
  }

})();
